import { utc } from '@date-fns/utc'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { toast } from 'sonner'

import { Checkbox, Pagination, Table } from 'slab'
import { calculateAgeAtDate } from 'utils'
import {
  getGetQuotingProspectsProspectIdCensusCensusIdEmployeesQueryKey as getEmployeesQueryKey,
  usePutQuotingProspectsProspectIdCensusCensusIdEmployeesId as useUpdateProspectEmployee,
} from 'utils/web_api'
import {
  type QuotingEmployee,
  type QuotingEmployeeCensus,
  type QuotingPaginatedEmployeeList,
} from 'utils/web_api.schemas'

import { emitEmployerOnboardingEvent } from '../../utils'

function formatDOB (dateOfBirth: string) {
  const date = new Date(dateOfBirth)

  return format(date, 'M/d/yyyy', {
    in: utc
  })
}

type CensusData = Pick<QuotingEmployeeCensus, 'quoting_prospect_id' | 'id'>

interface CensusEmployeesTableProps {
  census: CensusData;
  employees: QuotingEmployee[];
  totalEmployees: number;
  pageSize: number;
  pageIndex: number;
  onPageChange: (pageIndex: number) => void;
}

export default function CensusEmployeesTable ({
  census,
  employees,
  totalEmployees,
  pageSize,
  pageIndex,
  onPageChange,
}: CensusEmployeesTableProps) {
  const queryClient = useQueryClient()
  const employeesQueryKey = getEmployeesQueryKey(
    census.quoting_prospect_id,
    census.id,
    { 'page[size]': pageSize, 'page[number]': pageIndex + 1 }
  )

  const { mutate: updateEmployeeMutation } = useUpdateProspectEmployee({
    mutation: {
      onError: () => {
        toast.error('Failed to update employee')
      },
      onSuccess: (updatedEmployee) => {
        queryClient.setQueryData(employeesQueryKey, (oldData: QuotingPaginatedEmployeeList) => {
          return {
            ...oldData,
            data: oldData.data?.map((employee) => {
              if (employee.id === updatedEmployee.id) {
                return updatedEmployee
              }
              return employee
            })
          }
        })
      }
    }
  })

  const updateEmployee = (employee: QuotingEmployee, changes: Partial<QuotingEmployee>) => {
    updateEmployeeMutation({
      prospectId: census.quoting_prospect_id,
      censusId: census.id,
      id: employee.id,
      data: {
        ...changes
      },
    })
  }

  return (
    <div className='bg-white rounded-b-2xl'>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.ColumnHeader sticky>Name</Table.ColumnHeader>
            <Table.ColumnHeader align='center'>Eligible?</Table.ColumnHeader>
            <Table.ColumnHeader>Zip code</Table.ColumnHeader>
            <Table.ColumnHeader>Date of birth</Table.ColumnHeader>
            <Table.ColumnHeader align='right'>Age</Table.ColumnHeader>
            <Table.ColumnHeader align='right'>Dependents</Table.ColumnHeader>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {employees.map((employee) => {
            return (
              <Table.Row key={employee.id}>
                <Table.RowHeader sticky>
                  <span className='sensitive'>
                    {employee.name}
                  </span>
                </Table.RowHeader>
                <Table.Cell align='center'>
                  <label className='-mv-2 p-2'>
                    <Checkbox
                      aria-label='Eligible'
                      checked={employee.eligible}
                      onChange={() => {
                        updateEmployee(employee, { eligible: !employee.eligible })
                        emitEmployerOnboardingEvent('[ER] Census review eligible employee toggled', {})
                      }}
                    />
                  </label>
                </Table.Cell>
                <Table.Cell>
                  <span className='sensitive'>
                    {employee.zip}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className='sensitive'>
                    {formatDOB(employee.date_of_birth)}
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  <span className='sensitive'>
                    {calculateAgeAtDate(employee.date_of_birth, new Date())}
                  </span>
                </Table.Cell>
                <Table.Cell align='right'>
                  {employee.dependents.length}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table>
      <div className='flex justify-end p-2'>
        <Pagination
          pageIndex={pageIndex}
          perPage={pageSize}
          totalRows={totalEmployees}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}
